import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageWrapper, { PageHero } from "../components/PageWrapper";
import LeadForm from "../components/LeadForm";
import api from "../lib/api";
import { GraduationCap, Globe2, Building2, MapPin, ArrowUpRight, Sparkles } from "lucide-react";

const FOCUS = [
  { icon: GraduationCap, title: "School & College Tours", desc: "Curated international academic tours with structured learning outcomes." },
  { icon: Globe2, title: "International Exposure", desc: "Programmes designed for cross-cultural and policy-aware leadership." },
  { icon: Building2, title: "Academic Immersion", desc: "University and faculty engagements, lectures, and lab exposure." },
  { icon: MapPin, title: "Cultural Exchange", desc: "Cohort-based cultural exchange with peer institutions." },
];

export default function EducationalTourismPage() {
  const [programs, setPrograms] = useState([]);
  useEffect(() => {
    api
      .get("/programs", { params: { category: "Educational Tourism" } })
      .then((r) => setPrograms(r.data || []))
      .catch(() => {});
  }, []);

  return (
    <PageWrapper testId="educational-tourism-page">
      <PageHero
        eyebrow="Educational Tourism"
        title="Experiential global learning beyond classrooms."
        description="School and college tours, international exposure programmes, academic immersion, cultural exchange, and industrial visits — engineered for curricular outcomes, not sightseeing."
        image="https://images.pexels.com/photos/32213218/pexels-photo-32213218.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      <section className="bg-white py-24 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
                Focus
              </div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light text-navy tracking-tight">
                Programmes built around academic intent.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-3 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {FOCUS.map((f, i) => (
                <div
                  key={f.title}
                  data-testid={`tourism-focus-${i}`}
                  className="border border-slate-200 p-7"
                >
                  <div className="h-10 w-10 bg-navy text-gold flex items-center justify-center">
                    <f.icon size={18} strokeWidth={1.5} />
                  </div>
                  <div className="mt-6 font-display text-lg text-navy">
                    {f.title}
                  </div>
                  <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {f.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== Diplomexa integration callout ===== */}
      <section
        data-testid="tourism-diplomexa"
        className="bg-white py-20 sm:py-24 border-t border-slate-200"
      >
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="bg-navy text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-navy opacity-30" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 p-8 sm:p-12 lg:p-16 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-3 px-4 py-2 border border-gold/40 bg-white/[0.03] text-[11px] uppercase tracking-[0.28em] text-gold">
                  <Sparkles size={12} /> Major Offering · Diplomexa
                </div>
                <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight">
                  Educational tourism, powered by Diplomexa.
                </h2>
                <p className="mt-5 text-white/75 leading-relaxed">
                  Diplomexa strengthens Destineze's educational tourism ecosystem by combining
                  international travel, leadership training, Model United Nations, global
                  conferences, debate programmes, and cultural exposure — all into one powerful
                  student development journey.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/diplomexa"
                    data-testid="tourism-diplomexa-explore"
                    className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3.5 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-gold-300 transition"
                  >
                    Explore Diplomexa Journeys
                    <ArrowUpRight size={14} />
                  </Link>
                  <a
                    href="https://diplomexa.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="tourism-diplomexa-visit"
                    className="inline-flex items-center gap-2 border border-white/30 hover:border-gold hover:text-gold px-6 py-3.5 text-xs uppercase tracking-[0.18em] font-semibold transition"
                  >
                    Visit diplomexa.ai
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5 flex items-center justify-center">
                <div className="bg-white p-7 sm:p-9 inline-flex flex-col items-center">
                  <img
                    src="/diplomexa-logo.png"
                    alt="Diplomexa"
                    data-testid="tourism-diplomexa-logo"
                    className="h-24 w-24 object-contain"
                  />
                  <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-gold-500">
                    Conference OS · MUN OS
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {programs.length ? (
        <section className="bg-[#F8FAFC] py-24 sm:py-28">
          <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
              Programmes
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light text-navy tracking-tight max-w-3xl">
              Selected international exposure programmes.
            </h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {programs.map((p) => (
                <div
                  key={p.id}
                  data-testid={`program-card-${p.id}`}
                  className="card-lift group bg-white border border-slate-200 overflow-hidden"
                >
                  {p.cover_image ? (
                    <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                      />
                    </div>
                  ) : null}
                  <div className="p-7">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
                      {p.location} · {p.duration}
                    </div>
                    <h3 className="mt-2 font-display text-xl text-navy">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                      {p.summary}
                    </p>
                    {p.highlights?.length ? (
                      <ul className="mt-4 space-y-1.5">
                        {p.highlights.map((h) => (
                          <li
                            key={h}
                            className="flex items-start gap-2 text-sm text-slate-700"
                          >
                            <span className="mt-2 h-[3px] w-3 bg-navy" />
                            {h}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="bg-navy text-white py-24 sm:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-navy opacity-30" />
        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold">
              Plan a programme
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light tracking-tight">
              Design a global exposure cohort with our team.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              Itineraries are co-designed with your academic team, anchored in
              real institutional outcomes — not generic itineraries.
            </p>
            <div className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] font-semibold text-gold">
              Co-designed with your faculty <ArrowUpRight size={16} />
            </div>
          </div>
          <div className="lg:col-span-7">
            <LeadForm
              inquiryType="tourism"
              variant="dark"
              testIdPrefix="tourism-lead-form"
              title="Plan an educational tour"
              subtitle="Tell us about your cohort. Our programme team will respond shortly."
            />
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
